// Who is reading, when the host product knows.
//
// The widget is anonymous by default (ADR 0005) and that is right for a partner
// page. Inside a Keycloak-SSO'd host — SkyMind, the Intervention client — it is
// not: those users carry entitlement roles, articles are filed against them,
// and until this module existed every read path was role-blind, so an Asset
// Management user saw the Shipment Management articles.
//
// The host names the reader one of two ways.
//
//   PULL (preferred). The host registers a function and is then done with it:
//
//     window.SkyCellKB.setTokenProvider(() =>
//       keycloak.updateToken(30).then(() => keycloak.token));
//
//   The widget asks for a token immediately before each read, so it never
//   carries a snapshot that went stale while the panel sat open, and the host
//   gets to refresh on demand rather than racing the expiry. Nothing has to be
//   re-pushed on rotation, and a signed-out reader is just a provider that
//   returns null — there is no logout hook to forget.
//
//   PUSH (kept; every existing integration uses it). The host hands over each
//   token as it gets one:
//
//     window.SkyCellKB.setAuthToken(keycloak.token)   // and again on refresh
//
// A registered provider wins: a host that has one has no reason to push, and
// mixing the two should not let a stale pushed token outrank a fresh pulled one.
//
// The token is never inspected for authorization here — that happens on the KB
// server, against the realm's JWKS (server/services/hostIdentity.ts). This
// module only resolves the current one, hands it to the read calls, and tells
// the panel when the READER changed.
//
// It is deliberately module state rather than part of WidgetConfig: a token
// rotates every few minutes and config is frozen at mount.

/** Fired on window whenever the reader changes — a new sign-in, a different
 *  user, or a sign-out. Not fired for a mere token rotation. */
export const AUTH_EVENT = "skycell-kb:auth";

/**
 * How the host answers "who is reading, right now?". May be async so the host
 * can refresh an expiring token before answering (keycloak.updateToken), and
 * may return null for a reader who is signed out.
 */
export type TokenProvider = () =>
  | string
  | null
  | undefined
  | Promise<string | null | undefined>;

let token: string | null = null;
let viewer: string | null = null;
let provider: TokenProvider | null = null;
let established = false;
let inFlight: Promise<string | null> | null = null;

/** The last resolved host token. Read paths should call resolveAuthToken()
 *  instead; this is for callers that cannot await (and for tests). */
export const authToken = (): string | null => token;

/**
 * A stable, opaque id for the current reader — the token's `sub`, read from the
 * payload WITHOUT verifying it.
 *
 * That is safe because of what it is used for: scoping the browser read cache
 * (cache.ts) so one user's role-filtered list is not served to the next person
 * to sign in on the same machine. A forged `sub` only mis-keys its own author's
 * cache. Nothing is authorized with it, and the server never sees it.
 *
 * Prefers `sub` over the token itself because the token rotates every few
 * minutes and the cache would be thrown away on every refresh.
 */
export const viewerId = (): string | null => viewer;

/**
 * Whether this embed is supposed to know who is reading — a provider is
 * registered, or a reader has been named at least once in this page's life.
 *
 * The read path needs this to fail CLOSED. Once a reader has been identified,
 * an empty answer (expired session, a refresh that failed, a sign-out) must not
 * drop the reads back to the anonymous path, because with a share token that
 * path is WIDER: it unlocks the app's entire published set. It stays identified
 * and earns a 401, which the panel answers by asking the reader to reload.
 */
export const identityExpected = (): boolean => established || provider !== null;

function subjectOf(jwt: string): string | null {
  try {
    const part = jwt.split(".")[1];
    if (!part) return null;
    const json = atob(part.replace(/-/g, "+").replace(/_/g, "/"));
    const payload = JSON.parse(json) as { sub?: unknown };
    return typeof payload.sub === "string" ? payload.sub : null;
  } catch {
    return null;
  }
}

/**
 * Adopt a freshly obtained token, from either path, and announce the change
 * only when the READER changed, not when the token did — a host that re-pushes
 * (or a provider that re-mints) a refreshed token every few minutes must not
 * make the panel reload the list each time.
 *
 * A token whose `sub` cannot be read still counts as an identity ("unknown"),
 * so arriving at one from no token at all is announced: the alternative is a
 * panel that never reloads off its role-blind first list.
 */
function adopt(next: string | null | undefined): string | null {
  const value = (next ?? "").trim() || null;
  if (value === token) return token;
  const before = viewer;
  token = value;
  viewer = value ? (subjectOf(value) ?? "unknown") : null;
  if (value) established = true;
  if (viewer !== before) window.dispatchEvent(new CustomEvent(AUTH_EVENT));
  return token;
}

/**
 * Register (or with null, remove) the host's token provider. The widget calls
 * it before each read and whenever the panel comes back into view.
 *
 * Registering one marks this embed as identified from that moment — before the
 * provider has ever been called — so a first read cannot slip through on the
 * anonymous path while the provider is still resolving.
 */
export function setTokenProvider(next: TokenProvider | null | undefined): void {
  provider = typeof next === "function" ? next : null;
  if (!provider) return;
  // A provider that replaces a pushed token must not leave the stale one in
  // place to be sent by the next read; the first resolve will fill it in.
  inFlight = null;
}

/**
 * The reader's current token, asking the provider when there is one.
 *
 * Concurrent callers share one provider call: the panel re-resolves on open
 * and each read resolves again, and asking the host three times in the same
 * tick is noise. A provider that throws (a refresh that could not reach
 * Keycloak) is treated as "no token" rather than failing the read — the read
 * then fails closed on its own, with a message about the session rather than
 * about a promise.
 */
export async function resolveAuthToken(): Promise<string | null> {
  if (!provider) return token;
  if (inFlight) return inFlight;
  const call = (async () => {
    try {
      return adopt(await provider?.());
    } catch (e) {
      console.warn(`[kb] host token provider failed: ${(e as Error).message}`);
      return adopt(null);
    } finally {
      inFlight = null;
    }
  })();
  inFlight = call;
  return call;
}

/**
 * Push path: set (or with a falsy value, clear) the reader's token.
 *
 * Ignored while a provider is registered — see the module header. Kept for the
 * hosts that integrated before setTokenProvider existed.
 */
export function setAuthToken(next: string | null | undefined): void {
  if (provider) return;
  adopt(next);
}

/** Test seam. */
export function _resetViewer(): void {
  token = null;
  viewer = null;
  provider = null;
  established = false;
  inFlight = null;
}
